// 一覧行・カンバンカードの各セルの中身（handoff-README ① の列定義）。個人情報（住所・口座）は出さない。
import { ageDays, deadlineInfo, lastOutAt } from "../../../../lib/giftingRules";
import { agoLabel, calDaysAgo, mdOf, type ListRow } from "./listModel";
import type { PipelineCtx } from "./model";

export interface RowView {
  contact: { c1: string; tone: "reply" | "late" | "none" | ""; c2: string };
  address: { text: string; kind: "need" | "err" | "wait" | "waitLate" | "plain" | "mute"; sub: string };
  ship: { text: string; tone: "red" | "ink" | "mute"; sub: string };
  post: { text: string; tone: "ink" | "mute"; sub: string; warn: boolean };
  deadline: { text: string; tone: "red" | "orange" | "sub" | "mute"; big: boolean; sub: string; tag: string };
  /** 段階が変わってからの日数 */
  elapsed: string;
  /** 'IG検知' など（段階が自動で進んだとき） */
  autoLabel: string | null;
  autoTitle: string | null;
}

const AUTO_SOURCE_LABEL: Record<string, string> = {
  form: "本人フォーム",
  oms: "倉庫",
  ig_api: "IG検知",
  system: "システム",
};

export function rowView(row: ListRow, ctx: PipelineCtx): RowView {
  const { e, action } = row;
  const now = ctx.nowIso;
  const outAt = lastOutAt(e);

  // 連絡
  let contact: RowView["contact"];
  if (action.key === "reply") {
    contact = {
      c1: `● 返信あり ${agoLabel(calDaysAgo(e.last_in_at, now))}`,
      tone: "reply",
      c2: outAt ? `送信 ${agoLabel(calDaysAgo(outAt, now))}` : "未送信",
    };
  } else if (action.key === "nudge") {
    const n = calDaysAgo(outAt ?? e.stage_changed_at, now) ?? 0;
    contact = { c1: `返信なし ${n}日`, tone: "late", c2: outAt ? `送信 ${mdOf(outAt)}` : "" };
  } else {
    contact = {
      c1: outAt ? `送信 ${agoLabel(calDaysAgo(outAt, now))}` : "未連絡",
      tone: outAt ? "" : "none",
      c2: !e.ig_linked ? "IG未連携" : e.last_in_at ? `受信 ${agoLabel(calDaysAgo(e.last_in_at, now))}` : "",
    };
  }

  // 住所・発送
  const needShip = e.stage === 4 && e.address_state === "received" && !e.ship_order_no;
  const shipErr = needShip && !!e.ship_validation && !e.ship_validation.ok;
  const sinceStage = calDaysAgo(e.stage_changed_at, now) ?? ageDays(e, now);
  let address: RowView["address"];
  if (needShip) {
    address = {
      text: shipErr ? "書式エラー" : "入力済",
      kind: shipErr ? "err" : "need",
      sub: e.address_source === "proxy" ? "代理入力" : e.address_source === "form" ? "本人フォーム" : "",
    };
  } else if (e.address_state === "received") {
    address = { text: "入力済", kind: "plain", sub: "" };
  } else if (e.address_state === "form_sent") {
    address = { text: `入力待ち ${sinceStage}日`, kind: sinceStage >= 3 ? "waitLate" : "wait", sub: "" };
  } else if (e.stage === 3 || e.stage === 4) {
    address = { text: "未送付", kind: "mute", sub: "" };
  } else {
    address = { text: "—", kind: "mute", sub: "" };
  }

  let ship: RowView["ship"];
  if (needShip) ship = { text: shipErr ? "要修正" : "未指示", tone: "red", sub: "" };
  else if (e.shipped_at) ship = { text: `発送 ${mdOf(e.shipped_at)}`, tone: "ink", sub: `${calDaysAgo(e.shipped_at, now) ?? 0}日経過` };
  else if (e.ship_order_no) ship = { text: "指示済", tone: "ink", sub: e.ship_order_no };
  else ship = { text: "—", tone: "mute", sub: "" };

  // 投稿
  let post: RowView["post"];
  if (e.post_count > 0) {
    const unchecked = e.pr_checked_count < e.post_count;
    post = { text: `${e.post_count}件`, tone: "ink", sub: unchecked ? "PR表記 未確認" : "確認済", warn: unchecked };
  } else if (e.stage === 5) post = { text: "未投稿", tone: "mute", sub: "", warn: false };
  else post = { text: "—", tone: "mute", sub: "", warn: false };

  // 制作期限（発送済の行では経過より目立たせる）
  const dl = deadlineInfo(e, now);
  const tag = e.reminder_count > 0 ? `リマインド済 ${e.reminder_count}回` : "";
  let deadline: RowView["deadline"];
  if (e.stage === 5 && e.post_count === 0 && dl) {
    const sub = `期限 ${mdOf(dl.dueYmd)}`;
    if (dl.overdue) deadline = { text: `超過${-dl.daysLeft}日`, tone: "red", big: true, sub, tag };
    else if (action.key === "remind") deadline = { text: `残り${dl.daysLeft}日`, tone: "orange", big: true, sub, tag };
    else deadline = { text: `残り${dl.daysLeft}日`, tone: "sub", big: false, sub, tag };
  } else if (e.stage >= 6 || (e.stage === 5 && e.post_count > 0)) {
    deadline = { text: "投稿済", tone: "mute", big: false, sub: "", tag };
  } else {
    deadline = { text: "—", tone: "mute", big: false, sub: "", tag };
  }

  const ev = e.stage < 8 ? ctx.autoByDeal.get(e.id) ?? null : null;
  const auto = ev && ev.to_stage === e.stage ? AUTO_SOURCE_LABEL[ev.source] ?? null : null;

  return {
    contact,
    address,
    ship,
    post,
    deadline,
    elapsed: `${sinceStage}日`,
    autoLabel: auto,
    autoTitle: ev?.note ?? null,
  };
}
